/**
 * Settle this chat (slice 4 spec §6.3, Paper T4 and T4b): a bottom sheet on
 * a phone, a dialog on a desktop. Each open task in the chat closes Done
 * (it's done) or Canceled (drop it); the crew stops following up. It stays
 * in Chats, and a new message opens it again.
 */
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { RadioGroup, RadioGroupItem } from "@crew/ui/components/radio-group";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorHint } from "../../app/api";
import type { TaskItem } from "../tasks/api";
import { taskStatus } from "../tasks/task-view";
import { threadKey, threadsKey, useThread } from "./api";

const LINE = "The crew stops following up. It stays in Chats, and a new message opens it again.";

type Outcome = "done" | "canceled";

/** `POST /api/threads/:id/settle`, then every view the chat or its tasks are on. */
function useSettle(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tasks: { id: string; outcome: Outcome }[]) =>
      api("POST", `/api/threads/${threadId}/settle`, { tasks }),
    onSuccess: () => {
      for (const key of [threadKey(threadId), threadsKey, ["tasks"], ["task"]]) {
        void client.invalidateQueries({ queryKey: key });
      }
    },
  });
}

const isOpen = (t: TaskItem) => t.stage !== "done" && t.stage !== "canceled";

function TaskChoice({
  task,
  value,
  onChange,
}: {
  task: TaskItem;
  value: Outcome;
  onChange: (v: Outcome) => void;
}) {
  const option = (v: Outcome, title: string, line: string) => (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 px-4 py-3.5",
        v === "canceled" && "border-t border-border",
      )}
    >
      <RadioGroupItem value={v} className="mt-0.5" />
      <span className="flex flex-col gap-0.5">
        <span className="text-[15px] leading-5 font-medium text-foreground">{title}</span>
        <span className="text-meta text-muted-foreground">{line}</span>
      </span>
    </label>
  );
  return (
    <div className="flex flex-col gap-2.5" data-settle-task={task.id}>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold tracking-[0.06em] text-copper uppercase">
          Open task · {taskStatus(task).label}
        </span>
        <span className="text-base leading-[22px] font-medium text-foreground">{task.title}</span>
      </div>
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as Outcome)}
        aria-label={`${task.title}: done or drop it`}
        className="gap-0 rounded-xl bg-card ring-1 ring-border"
      >
        {option("done", "It's done", "The task moves to Done.")}
        {option("canceled", "Drop it", "The task is canceled.")}
      </RadioGroup>
    </div>
  );
}

export function SettleForm({
  threadId,
  mobile,
  onClose,
}: {
  threadId: string;
  mobile: boolean;
  onClose: () => void;
}) {
  const { data } = useThread(threadId);
  const open = (data?.tasks ?? []).filter(isOpen);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const settle = useSettle(threadId);
  const submit = () =>
    settle.mutate(
      open.map((t) => ({ id: t.id, outcome: outcomes[t.id] ?? "done" })),
      { onSuccess: onClose },
    );
  const Footer = mobile ? DrawerFooter : DialogFooter;
  const keep = (
    <Button variant="ghost" onClick={onClose} disabled={settle.isPending}>
      Keep it open
    </Button>
  );
  return (
    <div className="flex flex-col gap-5" data-settle>
      {open.length > 0 ? (
        <div className={cn("flex flex-col gap-4", mobile && "px-4")}>
          {open.map((t) => (
            <TaskChoice
              key={t.id}
              task={t}
              value={outcomes[t.id] ?? "done"}
              onChange={(v) => setOutcomes((o) => ({ ...o, [t.id]: v }))}
            />
          ))}
        </div>
      ) : null}
      {settle.error ? (
        <p role="alert" className={cn("text-meta text-destructive", mobile && "px-4")}>
          {errorHint(settle.error)}
        </p>
      ) : null}
      <Footer>
        {mobile ? null : keep}
        <Button onClick={submit} disabled={!data || settle.isPending}>
          {settle.isPending ? "Settling…" : "Settle"}
        </Button>
        {mobile ? keep : null}
      </Footer>
    </div>
  );
}

export function SettleWindow({
  threadId,
  open,
  onOpenChange,
}: {
  threadId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mobile = useIsMobile();
  const close = () => onOpenChange(false);
  const form = open ? <SettleForm threadId={threadId} mobile={mobile} onClose={close} /> : null;
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent showCloseButton className="pb-[env(safe-area-inset-bottom)]">
          <DrawerHeader className="pb-5">
            <DrawerTitle>Settle this chat</DrawerTitle>
            <DrawerDescription>{LINE}</DrawerDescription>
          </DrawerHeader>
          {form}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Settle this chat</DialogTitle>
          <DialogDescription>{LINE}</DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}
